import { useSyncExternalStore } from 'react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { RouterProvider } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import { NavigationProgress } from '@mantine/nprogress';
import { QueryProvider } from '@/providers/QueryProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { PostHogIdentitySync } from '@/components/analytics/PostHogIdentitySync';
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal';
import { posthog } from '@/lib/posthog';
import { isPaymentPage } from '@/lib/paymentTelemetryPrivacy';
import { useCustomerAppearance } from '@/hooks/useCustomerAppearance';
import { theme } from '@/theme';
import { router } from '@/routes/router';

import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/nprogress/styles.css';

function subscribeRoute(onChange: () => void) {
  return router.subscribe(onChange);
}

function isCustomerRoute() {
  return isPaymentPage(router.state.location.pathname);
}

export default function App() {
  const customerRoute = useSyncExternalStore(subscribeRoute, isCustomerRoute);
  const customerAppearance = useCustomerAppearance();

  return (
    <MantineProvider theme={theme} defaultColorScheme="light" forceColorScheme={customerRoute ? customerAppearance.colorScheme : undefined}>
      <NavigationProgress color="var(--accent)" size={2} />
      <PostHogProvider client={posthog}>
        {/* Bottom-right, not top-right: a toast that stays on screen (a blocked send, send warnings)
            would otherwise sit over the quote header's "Quote actions" menu. refined-ink.css lifts
            the bottom containers clear of the wizard's sticky footer and the phone tab bar. */}
        <Notifications position="bottom-right" limit={3} />
        <AuthProvider>
          <PostHogIdentitySync />
          <SessionExpiredModal />
          <ModalsProvider
            modalProps={{ radius: 'md', centered: true }}
            labels={{ confirm: 'Confirm', cancel: 'Cancel' }}
          >
            <QueryProvider>
              <RouterProvider router={router} />
            </QueryProvider>
          </ModalsProvider>
        </AuthProvider>
      </PostHogProvider>
    </MantineProvider>
  );
}
